<template>
  <div>
    <Head title="SKUs" />
    <h1 class="mb-8 text-3xl font-bold">Delivery Fees</h1>
    <div class="flex items-center justify-between mb-6">
      <search-filter v-model="form.search" class="mr-4 w-full max-w-md" @reset="reset" />
      <Link class="btn-orange" href="#/delivery-fees/create">
        <span>Add</span>
        <span class="hidden md:inline">&nbsp;Sku Fees</span>
      </Link>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-3 pt-3 px-3">Part Number</th>
          <th class="pb-3 pt-3 px-3">Part Description</th>
          <th class="pb-3 pt-3 px-3">Region</th>
          <th class="pb-3 pt-3 px-3">Channel</th>
          <th class="pb-3 pt-3 px-3 text-center">Amount</th>
          <th class="pb-3 pt-3 px-3 text-center">Subsequent Amount</th>
          <th class="pb-3 pt-3 px-3">User</th>
          <th class="pb-3 pt-3 px-3">Date</th>
          <th class="pb-3 pt-3 px-3" colspan="2" />
        </tr>
        <tr v-for="sku in fees.data" :key="sku.id" class="hover:bg-orange-100 focus-within:bg-gray-100">
          <td class="border-t">
            <a class="flex items-center px-3 py-3 focus:text-orange-500" href="#" @click="openUpdateFees(sku)">
              {{ sku.name }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center px-3 py-3" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.description }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center px-3 py-3" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.delivery_category }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center px-3 py-3" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.channel }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center justify-end w-full" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.amount }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center justify-end w-full" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.subsequent_amount }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center w-full" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.user }}
            </a>
          </td>
          <td class="border-t">
            <a class="flex items-center justify-center w-full" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              {{ sku.date }}
            </a>
          </td>
          <td class="w-px border-t">
            <a class="flex items-center px-3" href="#" tabindex="-1" @click="openUpdateFees(sku)">
              <icon name="cheveron-right" class="block w-6 h-6 fill-gray-400" />
            </a>
          </td>
        </tr>
        <tr v-if="fees.data.length === 0">
          <td class="px-3 py-3 border-t" colspan="4">No data found</td>
        </tr>
      </table>
    </div>
    <pagination class="mt-6" :links="fees.links" />
  </div>
  <div v-if="isOpen" class="fixed z-10 inset-0 overflow-y-auto">
    <div class="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <div class="fixed inset-0 transition-opacity">
        <div class="absolute inset-0 bg-gray-500 opacity-75" />
      </div>
      <!-- This element is to trick the browser into centering the modal contents. -->
      <span class="hidden sm:inline-block sm:align-middle sm:h-screen" />
      <div class="inline-block align-bottom bg-white w-1/2 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle" role="dialog" aria-modal="true" aria-labelledby="modal-headline">
        <form>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex modal-wrapper">
            <h1 style="font-size: 20px;">
              Update SKU Fees
            </h1>
          </div>
          <div class="bg-white px-4 pt-0 pb-4 sm:p-6 sm:pb-4">
            <div class="mb-4">
              <form @submit.prevent="update">
                <div class="flex flex-wrap -mb-8 -mr-6 pt-2 pl-2 pr-8 pb-8">
                  <p class="pb-1">Select SKU</p>
                  <multiselect
                    id="source"
                    v-model="formData.sku"
                    placeholder="Search SKUS"
                    :options="skus"
                    :clear-on-select="false"
                    :preserve-search="true"
                    :searchable="true"
                    class="mt-1 w-1/2"
                    :close-on-select="true"
                    :create-option="true"
                    :error="$props.errors.sku"
                    :style="$props.errors.sku ? 'border: var(--ms-border-width,1px) solid var(--ms-border-color,red)' : ''"
                  />
                  <p v-if="$props.errors" class="text-red-700 pt-2"><small>{{ $props.errors.sku }}</small></p>
                </div>
                <div class="flex flex-wrap -mb-8 -mr-6 pt-4 pl-2 pr-2 pb-10">
                  <text-input v-model="formData.amount" :error="$props.errors.amount" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Amount" />
                  <text-input v-model="formData.subsequent_amount" :error="$props.errors.subsequent_amount" class="pb-8 pr-6 w-1/2 lg:w-1/2" label="Subsequent Amount" />
                </div>
              </form>
            </div>
          </div>
          <div class="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <span class="flex w-full rounded-md shadow-sm sm:ml-3 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-transparent px-4 py-2 bg-green-600 text-base leading-6 font-medium text-white shadow-sm hover:bg-green-500 focus:outline-none focus:border-green-700 focus:shadow-outline-red sm:text-sm sm:leading-5" @click="update">
                Save
              </button>
            </span>
            <span class="mt-3 flex w-full rounded-md shadow-sm sm:mt-0 sm:w-auto">
              <button type="button" class="inline-flex justify-center w-full rounded-md border border-gray-300 px-4 py-2 bg-white text-base leading-6 font-medium text-gray-700 shadow-sm hover:text-gray-500 focus:outline-none focus:border-blue-300 focus:shadow-outline-blue sm:text-sm sm:leading-5" @click="closeModal">
                Close
              </button>
            </span>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
  
<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
import Multiselect from '@vueform/multiselect'
  
export default {
  components: {
    Head,
    Icon,
    Link,
    SearchFilter,
    Pagination,
    TextInput,
    Multiselect,
  },
  layout: Layout,
  props: {
    filters: Object,
    fees: Array,
    skus: Object,
    errors: Object,
  },
  data() {
    return {
      isOpen: false,
      selectedItem: null,
      formData: {
        sku: null,
        amount: null,
        subsequent_amount: null,
      },
      form: {
        search: this.filters.search,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function () {
        this.$inertia.get('/delivery-fees', pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    openUpdateFees(item) {
      this.selectedItem = item
      this.formData.sku = item.sku_id
      this.formData.amount = item.amount
      this.formData.subsequent_amount = item.subsequent_amount
      this.isOpen = true
    },
    closeModal() {
      this.isOpen = false
    },
    update() {
      this.formData._method = 'put'
      this.formData.delivery_fee_id = this.selectedItem.id
      this.$inertia.post(`/delivery-fees/${this.formData.delivery_fee_id}`, this.formData, {
        onSuccess: () => {
          this.delivery_fee_id = null
          this.formData.sku = null
          this.formData.amount = null
          this.formData.subsequent_amount = null
          this.isOpen = false
        },
      })
    },
  },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
<style type="text/css">
  .multiselect-option.is-selected.is-pointed {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
  .multiselect-option.is-selected {
    background: var(--ms-option-bg-selected-pointed,#fb923c);
    color: var(--ms-option-color-selected-pointed,#fff);
  }
</style>
  